import { useCallback, useEffect, useMemo, useState } from "react";

import { api, ApiFailure } from "../api/client";
import type { Usage, UsageRow } from "../api/types";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "../components/data-table";
import {
  Card, EmptyState, ErrorState, Field, Loading, PageHeader, inputClass,
} from "../components/ui";
import { formatSeconds, money } from "./Dashboard";

// Consumo: a dónde se fue el dinero, por día y por destino.
//
// # Por qué una gráfica de barras hecha a mano
//
// Porque la alternativa es una librería de gráficas, y la gráfica que hace falta
// aquí es una barra por día con el gasto. Añadir trescientos kilobytes al bundle
// para dibujar quince rectángulos no sale a cuenta, y el CSS hace esto bien.
export function UsagePage() {
  const [usage, setUsage] = useState<Usage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [prefixLength, setPrefixLength] = useState(4);

  const from = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString();
  }, [days]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get<Usage>(
        `/api/v1/usage?from=${encodeURIComponent(from)}&prefix_length=${prefixLength}`,
      );
      setUsage(data);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "error inesperado");
    }
  }, [from, prefixLength]);

  useEffect(() => {
    void load();
  }, [load]);

  const columnas = useMemo<ColumnDef<UsageRow, any>[]>(
    () => [
      {
        id: "day",
        accessorKey: "day",
        header: "Día",
        meta: { label: "Día", width: "8rem" },
        cell: ({ row }) => <span className="text-xs text-slate-600">{row.original.day}</span>,
      },
      {
        id: "destination",
        accessorKey: "destination",
        header: "Destino",
        meta: { label: "Destino" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-100">+{row.original.destination}</span>
        ),
      },
      {
        id: "calls",
        accessorKey: "calls",
        header: "Llamadas",
        meta: { label: "Llamadas", align: "right", width: "7rem" },
        cell: ({ row }) => (
          <span className="tabular-nums text-slate-600">{row.original.calls}</span>
        ),
      },
      {
        id: "answered_seconds",
        accessorKey: "answered_seconds",
        header: "Contestados",
        meta: { label: "Contestados", align: "right", hideOnMobile: true, width: "8rem" },
        cell: ({ row }) => (
          <span className="text-xs tabular-nums text-slate-600">
            {formatSeconds(row.original.answered_seconds)}
          </span>
        ),
      },
      {
        id: "billable_seconds",
        accessorKey: "billable_seconds",
        header: "Facturables",
        meta: { label: "Facturables", align: "right", width: "8rem" },
        cell: ({ row }) => (
          <span className="text-xs tabular-nums text-slate-600">
            {formatSeconds(row.original.billable_seconds)}
          </span>
        ),
      },
      {
        // Se ordena por los micros y se pinta el importe: `charged` es una
        // cadena y ordenaría «9.00» por encima de «10.00».
        id: "charged_micros",
        accessorKey: "charged_micros",
        header: "Importe",
        meta: { label: "Importe", align: "right", width: "8rem" },
        cell: ({ row }) => (
          <span className="font-mono text-xs tabular-nums text-slate-100">
            {money(row.original.charged)}
          </span>
        ),
      },
    ],
    [],
  );

  if (usage === null && error === null) return <Loading />;
  if (usage === null) return <ErrorState message={error ?? ""} onRetry={() => void load()} />;

  const serie = serieDiaria(usage);

  // La URL del CSV la construye el navegador con la misma sesión: es un GET, y
  // la cookie va sola. Un botón que descargara por fetch tendría que armar un
  // blob y un enlace temporal para el mismo resultado.
  const csvURL =
    `/api/v1/usage?from=${encodeURIComponent(from)}` +
    `&prefix_length=${prefixLength}&format=csv`;

  return (
    <div>
      <PageHeader
        title="Consumo"
        description={`Del ${formatDay(usage.from)} al ${formatDay(usage.to)}, en la zona ${usage.timezone}.`}
        action={
          <a
            href={csvURL}
            className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm font-medium text-slate-300 transition hover:bg-white/[0.09] hover:text-white"
          >
            Exportar CSV
          </a>
        }
      />

      <Card className="mb-4 flex flex-wrap items-end gap-4 p-4">
        <Field label="Periodo">
          <select
            className={inputClass}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
        </Field>
        <Field label="Agrupar destino por">
          <select
            className={inputClass}
            value={prefixLength}
            onChange={(e) => setPrefixLength(Number(e.target.value))}
          >
            <option value={2}>2 dígitos (país)</option>
            <option value={4}>4 dígitos (país y operador)</option>
            <option value={6}>6 dígitos (ciudad)</option>
          </select>
        </Field>
        <div className="ml-auto text-right">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total</p>
          <p className="text-2xl font-semibold tabular-nums text-slate-100">
            {money(usage.total)} <span className="text-base font-normal text-slate-500">{usage.currency}</span>
          </p>
        </div>
      </Card>

      {usage.rows.length === 0 ? (
        <EmptyState
          title="Sin consumo en el periodo"
          description="No hay llamadas contestadas que cobrar en estas fechas."
        />
      ) : (
        <>
          <section>
            <h2 className="mb-2 text-sm font-medium text-slate-100">Gasto por día</h2>
            <GastoPorDia serie={serie} currency={usage.currency} />
          </section>

          <section className="mt-6">
            <h2 className="mb-2 text-sm font-medium text-slate-100">Detalle</h2>
            <p className="mb-3 text-xs text-slate-500">
              Los segundos facturables ya llevan aplicadas las reglas de redondeo de la
              tarifa, así que pueden ser más que los contestados.
            </p>
            <DataTable<UsageRow>
              data={usage.rows}
              columns={columnas}
              getRowId={(fila) => `${fila.day}-${fila.destination}`}
              caption="Consumo por día y destino"
              empty={{ title: "Sin consumo en el periodo" }}
              search={{ placeholder: "Buscar por destino o día…" }}
              // El periodo entero viene en una sola respuesta, así que la
              // tabla reparte en páginas lo que ya está en memoria.
              pagination="client"
              pageSize={50}
              hideableColumns
              urlStateKey="consumo"
            />
          </section>
        </>
      )}
    </div>
  );
}

type DiaDeGasto = { day: string; charged_micros: number; calls: number };

/**
 * serieDiaria arma la serie de la gráfica: un punto por día del periodo.
 *
 * **Todos** los días, no solo los que tuvieron consumo. Con solo los que lo
 * tuvieron, dos barras contiguas pueden estar a tres semanas una de otra y el
 * eje de tiempo miente: se lee como actividad continua lo que fueron dos días
 * sueltos.
 *
 * Y de más antiguo a más reciente, que es como se lee el tiempo. La respuesta
 * viene al revés —lo último primero, que es lo que sirve en la tabla—.
 */
function serieDiaria(usage: Usage): DiaDeGasto[] {
  const porDia = new Map<string, DiaDeGasto>();

  const final = new Date(`${usage.to.slice(0, 10)}T00:00:00Z`);
  let cursor = new Date(`${usage.from.slice(0, 10)}T00:00:00Z`);
  // Tope defensivo: un periodo absurdo por un parámetro mal puesto no debe
  // colgar la pestaña dibujando diez mil barras.
  for (let i = 0; cursor <= final && i < 400; i += 1) {
    const dia = cursor.toISOString().slice(0, 10);
    porDia.set(dia, { day: dia, charged_micros: 0, calls: 0 });
    cursor = new Date(cursor.getTime() + 86_400_000);
  }

  for (const row of usage.rows) {
    const actual = porDia.get(row.day) ?? { day: row.day, charged_micros: 0, calls: 0 };
    actual.charged_micros += row.charged_micros;
    actual.calls += row.calls;
    porDia.set(row.day, actual);
  }

  return [...porDia.values()].sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * GastoPorDia dibuja una barra por día.
 *
 * # La trampa de las alturas en porcentaje
 *
 * Las barras se dibujaban con `height: N%` dentro de una columna de altura
 * automática, y **un porcentaje contra una altura automática vale cero**: la
 * gráfica era un rectángulo vacío con dos fechas debajo. Por eso cada columna
 * lleva `h-full`: la altura del contenedor sí es definida (`h-44`), y es
 * contra ella contra la que el porcentaje significa algo.
 *
 * # Un día sin gasto no es una barra diminuta
 *
 * Es ninguna barra, y una línea en la base para que el día siga ocupando su
 * sitio. El mínimo de 2 % es solo para los días que **sí** gastaron: ahí una
 * barra invisible se leería como un día sin llamadas, que es otra cosa.
 */
function GastoPorDia({ serie, currency }: { serie: DiaDeGasto[]; currency: string }) {
  const pico = Math.max(1, ...serie.map((d) => d.charged_micros));
  const diaPico = serie.find((d) => d.charged_micros === pico);
  const conGasto = serie.filter((d) => d.charged_micros > 0).length;

  return (
    <Card className="p-4">
      <div className="mb-2 flex items-baseline justify-between gap-3 text-xs">
        <span className="text-slate-500">
          {conGasto === 0
            ? "Sin gasto en el periodo"
            : `${conGasto} ${conGasto === 1 ? "día" : "días"} con gasto`}
        </span>
        {diaPico !== undefined && pico > 1 && (
          <span className="text-slate-500">
            máximo{" "}
            <span className="font-mono tabular-nums text-slate-300">
              {money(microsToString(pico))}
            </span>{" "}
            el {formatShortDay(diaPico.day)}
          </span>
        )}
      </div>

      <div
        className="flex h-44 items-end gap-px border-b border-white/10"
        role="img"
        aria-label={`Gasto diario de ${serie.length} días. Máximo ${money(
          microsToString(pico),
        )} ${currency}. El detalle está en la tabla de abajo.`}
      >
        {serie.map((dia, i) => (
          <div
            key={dia.day}
            // `h-full` es lo que da altura definida a la columna; sin ella el
            // porcentaje de la barra no resuelve y no se ve nada.
            className="group relative flex h-full flex-1 items-end"
            title={`${formatShortDay(dia.day)}: ${money(microsToString(dia.charged_micros))} ${currency}`}
          >
            {dia.charged_micros > 0 ? (
              <div
                className="w-full rounded-t-[4px] bg-sky-400/70 transition-colors group-hover:bg-sky-300"
                style={{ height: `${Math.max(2, (dia.charged_micros / pico) * 100)}%` }}
              />
            ) : (
              <div className="h-px w-full bg-white/[0.07]" />
            )}

            {/* La etiqueta se ancla al lado que la deja dentro de la tarjeta:
                centrada, la del último día se saldría y la página entera
                acabaría con desplazamiento horizontal. */}
            <div
              className={
                "pointer-events-none absolute bottom-full z-10 mb-1 hidden w-max rounded-lg border border-white/10 bg-[#11162a] px-2 py-1 text-xs shadow-xl group-hover:block " +
                (i < serie.length / 2 ? "left-0" : "right-0")
              }
            >
              <div className="text-slate-400">{formatShortDay(dia.day)}</div>
              <div className="font-mono tabular-nums text-slate-100">
                {money(microsToString(dia.charged_micros))} {currency}
              </div>
              <div className="text-slate-500">
                {dia.calls} {dia.calls === 1 ? "llamada" : "llamadas"}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-2 flex justify-between text-[11px] text-slate-600">
        <span>{formatShortDay(serie[0]?.day)}</span>
        <span>{formatShortDay(serie[serie.length - 1]?.day)}</span>
      </div>
    </Card>
  );
}

function formatShortDay(day: string | undefined): string {
  if (day === undefined) return "";
  return new Date(`${day}T12:00:00Z`).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
  });
}

function microsToString(micros: number): string {
  return (micros / 1_000_000).toFixed(6);
}

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit", month: "short", year: "numeric",
  });
}
